function evaluateXpath<T extends Node>(
  contextNode: Node,
  xpathExpression: string,
  options: { requireDocumentOrder?: boolean } = {},
): T[] {
  const result = document.evaluate(
    xpathExpression,
    contextNode,
    null,
    options.requireDocumentOrder
      ? XPathResult.ORDERED_NODE_ITERATOR_TYPE
      : XPathResult.ANY_TYPE,
    null,
  )

  const foundNodes = []

  let node = result.iterateNext()
  while (node) {
    foundNodes.push(node)
    node = result.iterateNext()
  }

  return foundNodes
}

function getCaseInsensitiveXPathTextSelector(
  attribute: string,
  text: string,
): string {
  return (
    `translate(@${attribute},` +
    `'ABCDEFGHIJKLMNOPQRSTUVWXYZ','abcdefghijklmnopqrstuvwxyz')='${text}'`
  )
}

function getCaseInsensitiveAttribute(attribute: string): string {
  return (
    `translate(@${attribute},` +
    `'ABCDEFGHIJKLMNOPQRSTUVWXYZ','abcdefghijklmnopqrstuvwxyz')`
  )
}

function getCaseInsensitiveString(str: string): string {
  return (
    `translate(${str},` +
    `'ABCDEFGHIJKLMNOPQRSTUVWXYZ','abcdefghijklmnopqrstuvwxyz')`
  )
}

function getCaseInsensitiveText(): string {
  return getCaseInsensitiveString('text()')
}

function getCaseInsensitiveXPathNodeTextSelector(text: string): string {
  return `${getCaseInsensitiveText()}='${text.toLowerCase()}'`
}

function getCommonAncestor<T extends HTMLElement>(
  elementA: HTMLElement,
  elementB: HTMLElement,
): T | null {
  const originalIdB = elementB.id
  elementB.setAttribute('id', `tempid-${Math.random().toString().slice(2)}`)

  const results = document.evaluate(
    `ancestor-or-self::*[count(descendant-or-self::*[@id="${elementB.id}"]) > 0]`,
    elementA,
    null,
    XPathResult.ORDERED_NODE_SNAPSHOT_TYPE,
    null,
  )
  const result = results.snapshotItem(results.snapshotLength - 1) as T

  if (originalIdB) {
    elementB.setAttribute('id', originalIdB)
  } else {
    elementB.removeAttribute('id')
  }

  return result
}

function getLevelsBetween(
  descendant: HTMLElement,
  ancestor: HTMLElement,
): number {
  let currentElement = descendant
  let depth = 0
  while (ancestor !== currentElement) {
    if (!currentElement) {
      throw new Error('Did not find ancestor')
    }

    currentElement = currentElement.parentElement
    depth += 1
  }

  return depth
}

function getNearestElementMatch<T extends HTMLElement>(
  element: HTMLElement,
  xpathPredicate: string,
  tiebreak: 'following' | 'preceding',
): T | null {
  const followingResults = document.evaluate(
    `following::${xpathPredicate}`,
    element,
    null,
    XPathResult.ORDERED_NODE_SNAPSHOT_TYPE,
    null,
  )
  const precedingResults = document.evaluate(
    `preceding::${xpathPredicate}`,
    element,
    null,
    XPathResult.ORDERED_NODE_SNAPSHOT_TYPE,
    null,
  )

  const following = followingResults.snapshotItem(0) as T
  const preceding = precedingResults.snapshotItem(
    precedingResults.snapshotLength - 1,
  ) as T

  let result: T | null
  if (!following && !preceding) {
    result = null
  } else if (following && !preceding) {
    result = following
  } else if (!following && preceding) {
    result = preceding
  } else {
    // First tiebreak - determine which element is "closer" to our original element
    //                  by seeing which has the closer common ancestor
    const followingCommonAncestor = getCommonAncestor(element, following)
    const precedingCommonAncestor = getCommonAncestor(element, preceding)

    const followingDistance = getLevelsBetween(element, followingCommonAncestor)
    const precedingDistance = getLevelsBetween(element, precedingCommonAncestor)

    if (followingDistance < precedingDistance) {
      result = following
    } else if (followingDistance > precedingDistance) {
      result = preceding
    } else {
      // Second tiebreak - use user provided tiebreak
      result = tiebreak === 'following' ? following : preceding
    }
  }

  if (result) {
    const privacyPartyElements = evaluateXpath(
      result,
      'ancestor-or-self::*[contains(@class, "PrivacyParty")]',
    )
    if (privacyPartyElements.length > 0) {
      return null
    }

    return result
  }
  return null
}

export {
  evaluateXpath,
  getCaseInsensitiveAttribute,
  getCaseInsensitiveXPathNodeTextSelector,
  getCaseInsensitiveXPathTextSelector,
  getCaseInsensitiveString,
  getCaseInsensitiveText,
  getCommonAncestor,
  getNearestElementMatch,
}
